// src/components/Navbar.js
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { logoutUser } from '../firebase/services';
import { isAdminEmail } from '../config/admins';

export default function Navbar() {
  const { user } = useAuth();
  const { lang, switchLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const isDashboard = location.pathname.startsWith('/dashboard')
    || location.pathname.startsWith('/generate')
    || location.pathname.startsWith('/certificates')
    || location.pathname.startsWith('/settings');

  const isHome  = location.pathname === '/';
  const isAdmin = isAdminEmail(user?.email);

  const handleLogout = async () => {
    await logoutUser();
    navigate('/');
    setMenuOpen(false);
  };

  const handleAnchor = (anchor) => {
    setMenuOpen(false);
    if (isHome) {
      document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/');
      setTimeout(() => {
        document.getElementById(anchor)?.scrollIntoView({ behavior: 'smooth' });
      }, 120);
    }
  };

  const navLinks = [
    { label: t('nav.features'),   anchor: 'features'    },
    { label: t('nav.howItWorks'), anchor: 'how-it-works'},
    { label: t('nav.industries'), anchor: 'industries'  },
    { label: t('nav.pricing'),    anchor: 'pricing'     },
  ];

  return (
    <>
      {/* ── Responsive CSS ── */}
      <style>{`
        .nav-links       { display: flex; gap: 28px; align-items: center; }
        .nav-right       { display: flex; align-items: center; gap: 16px; }
        .nav-burger      { display: none; flex-direction: column; gap: 5px;
                           background: none; border: none; cursor: pointer; padding: 6px; }
        .nav-mobile-menu { display: none; }

        /* Hamburger icon animation */
        .nav-burger .bar { display: block; width: 24px; height: 2px;
                           background: #fff; border-radius: 2px;
                           transition: transform 0.25s, opacity 0.25s; }
        .nav-burger.open .bar:nth-child(1) { transform: translateY(7px) rotate(45deg); }
        .nav-burger.open .bar:nth-child(2) { opacity: 0; }
        .nav-burger.open .bar:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }

        @media (max-width: 768px) {
          .nav-links  { display: none; }
          .nav-right  { display: none; }
          .nav-burger { display: flex; }

          .nav-mobile-menu {
            display: flex;
            flex-direction: column;
            position: fixed;
            top: 64px;
            left: 0; right: 0;
            background: rgba(8,20,60,0.99);
            backdrop-filter: blur(16px);
            border-top: 1px solid rgba(255,255,255,0.08);
            padding: 12px 0 24px;
            z-index: 999;
            max-height: calc(100vh - 64px);
            overflow-y: auto;
            animation: slideDown 0.2s ease;
          }
          .nav-mobile-menu.closed { display: none; }

          @keyframes slideDown {
            from { opacity: 0; transform: translateY(-8px); }
            to   { opacity: 1; transform: translateY(0); }
          }

          .mob-item {
            display: flex; align-items: center;
            width: 100%; padding: 14px 24px;
            color: rgba(255,255,255,0.85);
            font-size: 16px; font-weight: 500;
            text-decoration: none;
            background: none; border: none; cursor: pointer;
            font-family: inherit; text-align: left;
            border-bottom: 1px solid rgba(255,255,255,0.05);
            transition: background 0.15s;
          }
          .mob-item:hover   { background: rgba(255,255,255,0.05); }
          .mob-item:active  { background: rgba(30,94,255,0.15); }

          .mob-divider { height: 1px; background: rgba(255,255,255,0.08); margin: 8px 0; }

          .mob-lang {
            display: flex; gap: 8px; padding: 12px 24px;
          }
          .mob-lang-btn {
            background: rgba(255,255,255,0.07);
            border: 1px solid rgba(255,255,255,0.12);
            color: rgba(255,255,255,0.55);
            font-size: 13px; font-weight: 700;
            padding: 7px 18px; border-radius: 8px;
            cursor: pointer; font-family: inherit;
            transition: all 0.2s;
          }
          .mob-lang-btn.active {
            background: rgba(30,94,255,0.25);
            border-color: rgba(30,94,255,0.5);
            color: #fff;
          }

          .mob-actions {
            display: flex; flex-direction: column; gap: 10px;
            padding: 12px 24px 0;
          }
          .mob-login {
            display: block; text-align: center;
            padding: 13px; border-radius: 12px;
            border: 1px solid rgba(255,255,255,0.15);
            color: #fff; text-decoration: none;
            font-size: 15px; font-weight: 600;
            background: rgba(255,255,255,0.05);
          }
          .mob-cta {
            display: block; text-align: center;
            padding: 13px; border-radius: 12px;
            background: linear-gradient(135deg,#1E5EFF,#0A3DD4);
            color: #fff; text-decoration: none;
            font-size: 15px; font-weight: 700;
            border: none; cursor: pointer; font-family: inherit;
          }
          .mob-logout {
            display: block; text-align: center;
            padding: 13px; border-radius: 12px;
            background: rgba(239,68,68,0.1);
            border: 1px solid rgba(239,68,68,0.25);
            color: #FCA5A5; font-size: 15px; font-weight: 600;
            cursor: pointer; font-family: inherit;
          }
          .mob-user {
            padding: 10px 24px 4px;
            font-size: 13px; color: rgba(255,255,255,0.45);
            word-break: break-all;
          }
        }
      `}</style>

      <nav style={st.nav}>
        <div style={st.inner}>
          {/* Logo */}
          <Link to="/" style={st.logo} onClick={() => setMenuOpen(false)}>
            <span style={st.logoMain}>COA</span>
            <span style={st.logoAccent}>Lab</span>
          </Link>

          {/* Desktop nav links */}
          <div className="nav-links">
            {!isDashboard && !user && navLinks.map(({ label, anchor }) => (
              <button key={anchor} style={st.linkBtn} onClick={() => handleAnchor(anchor)}>
                {label}
              </button>
            ))}
            {user && (
              <>
                <Link to="/dashboard"    style={st.link}>{t('nav.dashboard')}</Link>
                <Link to="/generate"     style={st.link}>{t('nav.generateCOA')}</Link>
                <Link to="/certificates" style={st.link}>{t('nav.certificates')}</Link>
                <Link to="/settings"     style={st.link}>{t('nav.settings')}</Link>
                {isAdmin && (
                  <Link to="/admin" style={{ ...st.link, color: '#FBBF24' }}>🛡️ Admin</Link>
                )}
              </>
            )}
          </div>

          {/* Desktop right side */}
          <div className="nav-right">
            <div style={st.langSwitch}>
              <button style={{ ...st.langBtn, ...(lang === 'en' ? st.langActive : {}) }} onClick={() => switchLang('en')}>EN</button>
              <span style={st.langDivider}>|</span>
              <button style={{ ...st.langBtn, ...(lang === 'de' ? st.langActive : {}) }} onClick={() => switchLang('de')}>DE</button>
            </div>
            {user ? (
              <div style={st.userArea}>
                <span style={st.userName}>{user.displayName || user.email}</span>
                <button onClick={handleLogout} style={st.logoutBtn}>{t('nav.logout')}</button>
              </div>
            ) : (
              <>
                <Link to="/login"    style={st.loginBtn}>{t('nav.login')}</Link>
                <Link to="/register" style={st.ctaBtn}>{t('nav.getStarted')}</Link>
              </>
            )}
          </div>

          {/* Hamburger button — visible only on mobile via CSS */}
          <button
            className={`nav-burger${menuOpen ? ' open' : ''}`}
            onClick={() => setMenuOpen(o => !o)}
            aria-label="Menu"
          >
            <span className="bar" />
            <span className="bar" />
            <span className="bar" />
          </button>
        </div>

        {/* ── Mobile menu ── */}
        <div className={`nav-mobile-menu${menuOpen ? '' : ' closed'}`}>

          {/* Nav links */}
          {!user && !isDashboard && navLinks.map(({ label, anchor }) => (
            <button key={anchor} className="mob-item" onClick={() => handleAnchor(anchor)}>
              {label}
            </button>
          ))}
          {user && (
            <>
              <Link to="/dashboard"    className="mob-item" onClick={() => setMenuOpen(false)}>{t('nav.dashboard')}</Link>
              <Link to="/generate"     className="mob-item" onClick={() => setMenuOpen(false)}>{t('nav.generateCOA')}</Link>
              <Link to="/certificates" className="mob-item" onClick={() => setMenuOpen(false)}>{t('nav.certificates')}</Link>
              <Link to="/settings"     className="mob-item" onClick={() => setMenuOpen(false)}>{t('nav.settings')}</Link>
              {isAdmin && (
                <Link to="/admin" className="mob-item" style={{ color: '#FBBF24' }} onClick={() => setMenuOpen(false)}>🛡️ Admin</Link>
              )}
            </>
          )}

          <div className="mob-divider" />

          {/* Language switcher */}
          <div className="mob-lang">
            <button className={`mob-lang-btn${lang === 'en' ? ' active' : ''}`} onClick={() => switchLang('en')}>EN — English</button>
            <button className={`mob-lang-btn${lang === 'de' ? ' active' : ''}`} onClick={() => switchLang('de')}>DE — Deutsch</button>
          </div>

          <div className="mob-divider" />

          {/* Auth buttons */}
          {user ? (
            <>
              <div className="mob-user">{user.displayName || user.email}</div>
              <div className="mob-actions">
                <button className="mob-logout" onClick={handleLogout}>{t('nav.logout')}</button>
              </div>
            </>
          ) : (
            <div className="mob-actions">
              <Link to="/login"    className="mob-login"  onClick={() => setMenuOpen(false)}>{t('nav.login')}</Link>
              <Link to="/register" className="mob-cta"    onClick={() => setMenuOpen(false)}>{t('nav.getStarted')}</Link>
            </div>
          )}
        </div>
      </nav>
    </>
  );
}

const st = {
  nav:        { position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, background: 'rgba(8,20,60,0.97)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(255,255,255,0.08)' },
  inner:      { maxWidth: 1200, margin: '0 auto', padding: '0 20px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo:       { display: 'flex', alignItems: 'baseline', gap: 2, textDecoration: 'none' },
  logoMain:   { fontSize: 22, fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' },
  logoAccent: { fontSize: 14, fontWeight: 600, color: '#4A9EFF', marginLeft: 1 },
  link:       { color: 'rgba(255,255,255,0.75)', textDecoration: 'none', fontSize: 14, fontWeight: 500 },
  linkBtn:    { background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.75)', fontSize: 14, fontWeight: 500, padding: 0, fontFamily: 'inherit' },
  langSwitch: { display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(255,255,255,0.07)', borderRadius: 8, padding: '4px 8px' },
  langBtn:    { background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 12, fontWeight: 600, cursor: 'pointer', padding: '2px 4px', borderRadius: 4 },
  langActive: { color: '#fff', background: 'rgba(74,158,255,0.25)' },
  langDivider:{ color: 'rgba(255,255,255,0.2)', fontSize: 12 },
  userArea:   { display: 'flex', alignItems: 'center', gap: 12 },
  userName:   { color: 'rgba(255,255,255,0.7)', fontSize: 13 },
  loginBtn:   { color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: 14, fontWeight: 500, padding: '8px 16px' },
  logoutBtn:  { background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: 13, padding: '6px 14px', borderRadius: 8, cursor: 'pointer' },
  ctaBtn:     { background: 'linear-gradient(135deg,#1E5EFF,#0A3DD4)', color: '#fff', textDecoration: 'none', fontSize: 14, fontWeight: 600, padding: '9px 20px', borderRadius: 10, border: 'none', cursor: 'pointer' },
};
